import { Box, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { CustomerUserDetails, ReportInstance } from '../../../backendsdk';
import { TrackedDataGrid as StyledDataGrid } from '../../../components/TrackedComponents';
import useIsMobile from '../../../hooks/isMobile';
import useProfile from '../../../hooks/profile';
import { getDateTimeColumnType, localeByCountryMap } from '../../../utils/DataGridDateTime';
import palette from '../../ColorPalette';
import { gridLocalization } from '../OTA/MuiDeviceTable';
import { REPORT_TYPE_TO_URL_PARAM } from './ReportComponent';
import { deviceFilter } from './ReportDetails';
import { createReportFilterDisplay } from './ReportFilterDisplay';

interface PastReportsProps {
    reportInstances: ReportInstance[];
    geofences: Record<string, string>;
    users?: CustomerUserDetails[];
    isLoading: boolean;
    onRowClick: CallableFunction;
}

const PastReports: React.FC<PastReportsProps> = (props: PastReportsProps) => {
    const { i18n, t } = useTranslation();
    const { profile } = useProfile();
    const isMobile = useIsMobile();
    const history = useHistory();
    const locale = localeByCountryMap[profile.locale];

    const { formatFilters, renderFilters, formatExtraFilters, renderExtraFilters } = useMemo(
        () =>
            createReportFilterDisplay({
                t,
                profileCustomerName: profile.customer.name,
                geofences: props.geofences,
                users: props.users,
            }),
        [t, profile.customer.name, props.geofences, props.users, i18n.languages],
    );

    const formatScope = (scope: string) => {
        return t(`content.reports.form.scope.${scope.toLocaleLowerCase()}`);
    };

    const columns: GridColDef[] = useMemo(() => {
        return [
            {
                field: 'created_at',
                headerName: t('content.reports.table.execution_date'),
                ...getDateTimeColumnType(locale),
                renderCell: (params) => {
                    return dayjs(params.value as Date).format(profile.dateTimeFormat);
                },
                flex: 1,
            },
            {
                field: 'scope',
                type: 'singleSelect',
                valueOptions: ['daily', 'weekly', 'monthly'].map((scope) => formatScope(scope)),
                headerName: t('content.reports.form.scope.header'),
                valueGetter: (params: GridRenderCellParams) => formatScope(params.value),
                flex: 1,
            },
            {
                field: 'vehicles',
                headerName: t('content.reports.columns.vehicles'),
                sortable: false,
                filterable: false,
                renderCell: (params: GridRenderCellParams) =>
                    renderFilters('filter', ...deviceFilter(params.row.filters), true),
                flex: 1,
            },
            {
                field: 'rules',
                headerName: t('content.reports.columns.rules'),
                sortable: false,
                renderCell: (params: GridRenderCellParams) => renderExtraFilters(params.row.instance),
                flex: 1,
            },
        ];
    }, [props.geofences, props.users, profile, t, locale, i18n.languages]);

    const rows = useMemo(() => {
        return props.reportInstances.map((reportInstance) => {
            return {
                id: reportInstance.pk,
                created_at: dayjs.unix(reportInstance.created_at).toDate(),
                type: reportInstance.report_type,
                scope: reportInstance.report_scope,
                vehicles: formatFilters(
                    'filter',
                    ...deviceFilter(reportInstance.filters as Record<string, Array<string> | string>),
                    true,
                ),
                rules: formatExtraFilters(reportInstance),
                filters: reportInstance.filters,
                instance: reportInstance,
            };
        });
    }, [props.reportInstances, props.geofences, props.users, profile, t, i18n.languages]);

    return (
        <Box sx={{ width: '100%', height: '100%' }}>
            <StyledDataGrid
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'created_at', sort: 'desc' }],
                    },
                }}
                id="reports-data-grid"
                data-testid="reports-data-grid"
                loading={props.isLoading}
                getRowHeight={() => 'auto'}
                columns={columns}
                rows={rows}
                disableRowSelectionOnClick
                hideFooter
                disableVirtualization={isMobile || process.env.JEST_WORKER_ID !== undefined}
                getCellClassName={(params) => `${params.field}-cell`}
                onRowClick={(params) => {
                    props.onRowClick();
                    history.push(`/reports/${REPORT_TYPE_TO_URL_PARAM[params.row.type]}/run/${params.row.id}`);
                }}
                slots={{
                    noRowsOverlay: () => (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant="overline">{t('content.reports.no_instances')}</Typography>
                        </Box>
                    ),
                }}
                slotProps={{
                    columnsPanel: {
                        sx: {
                            '& .MuiDataGrid-panelFooter button:first-child': {
                                display: 'none',
                            },
                        },
                    },
                }}
                localeText={{
                    ...gridLocalization[i18n.languages[0]],
                    filterPanelRemoveAll: t('table.remove_all'),
                    columnMenuManageColumns: t('table.manage_columns'),
                    unpin: t('table.unpin'),
                }}
                columnHeaderHeight={42}
                sx={{
                    height: '100%',
                    width: '100%',
                    pb: 2,
                    '& .MuiDataGrid-main': {
                        borderRadius: '4px',
                        boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.25)',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        borderBottom: 'none',
                        color: palette.neutral[100],
                        '& .MuiSvgIcon-root': {
                            fill: palette.neutral[100],
                        },
                    },
                    '& .MuiDataGrid-row:hover': {
                        cursor: 'pointer',
                    },
                    '& .MuiDataGrid-cell--editing': {
                        padding: '0 !important',
                    },
                    '& .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                    },
                    '& .MuiDataGrid-row--dynamicHeight': { minHeight: '52px !important' },
                    '&.MuiDataGrid-root .MuiDataGrid-cell': { py: '6px' },
                    backgroundColor: palette.neutral[50],
                    minHeight: 0,
                }}
            />
        </Box>
    );
};

export default PastReports;
